import { AfterViewInit, Component, ElementRef, OnDestroy, OnInit, ViewChild, signal } from '@angular/core';
import { FormBuilder, FormGroup, ReactiveFormsModule, Validators } from '@angular/forms';
import { Router } from '@angular/router';
import { TranslateModule, TranslateService } from '@ngx-translate/core';
import { TablerIconsModule } from 'angular-tabler-icons';
import { finalize } from 'rxjs';
import { CompanyService } from 'src/app/services/company.service';
import { ToastService } from 'src/app/services/toast.service';
import { CaptchaComponent } from 'src/app/shared/components/captcha/captcha.component';
import { LanguageDropdownComponent } from 'src/app/shared/components/language-dropdown/language-dropdown.component';
import { REGEX_PATTERNS } from 'src/app/shared/constants';

@Component({
  selector: 'app-landing-page',
  imports: [TablerIconsModule, ReactiveFormsModule, LanguageDropdownComponent, CaptchaComponent, TranslateModule],
  templateUrl: './landing-page.component.html',
  styleUrls: ['./landing-page.component.scss']
})
export class LandingPageComponent implements OnInit, AfterViewInit, OnDestroy {
  @ViewChild('top') topElement!: ElementRef<HTMLElement>;
  @ViewChild('registration') registerElement!: ElementRef<HTMLElement>;
  @ViewChild('contactSection') private contactSection!: ElementRef<HTMLElement>;
  @ViewChild(CaptchaComponent) private captcha!: CaptchaComponent;

  contactForm!: FormGroup;
  isSubmitting = false;
  readonly showScrollToTop = signal(false);
  private contactObserver?: IntersectionObserver;
  readonly currentYear = new Date().getFullYear();
  readonly inputFields = [
    { name: 'firstName', label: 'first name', type: 'text', autocomplete: 'given-name', inputmode: 'text' },
    { name: 'lastName', label: 'last name', type: 'text', autocomplete: 'family-name', inputmode: 'text' },
    { name: 'email', label: 'email', type: 'email', autocomplete: 'email', inputmode: 'email' },
    { name: 'mobile', label: 'mobile', type: 'tel', autocomplete: 'tel', inputmode: 'numeric' },
    { name: 'companyName', label: 'company name', type: 'text', autocomplete: 'organization', inputmode: 'text' },
    { name: 'organizationNumber', label: 'organization number', type: 'text', autocomplete: 'off', inputmode: 'numeric' }
  ] as const;
  readonly registrationSteps = [
    { title: 'send in the form', description: 'fill in information about you and the company' },
    { title: 'we confirm the request', description: 'we will contact you as soon as possible' },
    { title: 'we guide you further', description: 'you will receive help through the next steps' }
  ];

  constructor(
    private router: Router,
    private formBuilder: FormBuilder,
    private translateService: TranslateService,
    private toastService: ToastService,
    private companyService: CompanyService
  ) {}

  ngOnInit(): void {
    const numericValidators = [Validators.required, Validators.maxLength(20), Validators.pattern('^[0-9]+$')];
    this.contactForm = this.formBuilder.group({
      firstName: [null, Validators.required],
      lastName: [null, Validators.required],
      email: [null, [Validators.required, Validators.pattern(REGEX_PATTERNS.EMAIL)]],
      mobile: [null, numericValidators],
      companyName: [null, Validators.required],
      organizationNumber: [null, numericValidators],
      message: [null],
      captcha: [null, Validators.required]
    });
  }

  ngAfterViewInit(): void {
    this.contactObserver = new IntersectionObserver(([entry]) => {
      this.showScrollToTop.set(entry.isIntersecting || entry.boundingClientRect.bottom <= 0);
    });
    this.contactObserver.observe(this.contactSection.nativeElement);
  }

  ngOnDestroy(): void {
    this.contactObserver?.disconnect();
  }

  scrollSmooth(element: ElementRef<HTMLElement>): void {
    const reducedMotion = window.matchMedia('(prefers-reduced-motion: reduce)').matches;
    element.nativeElement.scrollIntoView({ behavior: reducedMotion ? 'auto' : 'smooth', block: 'start' });
  }

  readonly onCaptchaInputChange = (value: string): void => {
    this.contactForm.patchValue({ captcha: value });
  };

  navigateLogin(): void {
    void this.router.navigate(['/login']);
  }

  get controls() {
    return this.contactForm.controls;
  }

  get language(): string {
    return this.translateService.currentLang || this.translateService.defaultLang || 'no';
  }

  fieldError(name: string, label: string): string | null {
    const control = this.controls[name];
    if (!control?.touched || !control.errors) return null;
    if (control.errors['required']) return label + ' is required';
    if (control.errors['pattern']) return label + ' is invalid';
    if (control.errors['maxlength']) {
      return name === 'mobile'
        ? 'mobile length must not exceed 20'
        : 'organization number length must not exceed 20 characters';
    }
    return control.errors['message'] ?? null;
  }

  onSubmit(): void {
    if (this.isSubmitting) return;
    this.contactForm.markAllAsTouched();
    if (!this.captcha.validateCaptcha() || this.contactForm.invalid) return;

    this.isSubmitting = true;
    this.companyService.addContactCompany(this.contactForm.value).pipe(
      finalize(() => {
        this.isSubmitting = false;
      })
    ).subscribe({
      next: () => {
        this.contactForm.reset();
        this.captcha.reset();
        this.toastService.success(this.translateService.instant('send successfully'));
      },
      error: (errors: unknown) => {
        if (errors && typeof errors === 'object') {
          for (const [name, messages] of Object.entries(errors)) {
            if (Array.isArray(messages) && typeof messages[0] === 'string') {
              this.controls[name]?.setErrors({ message: messages[0] });
            }
          }
        }
        this.captcha.reset();
      }
    });
  }
}
